import { type ButtonHTMLAttributes, type MouseEvent, useRef, useState } from 'react';

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  dwellSeconds: number;
  onActivate: () => void;
  tone?: 'standard' | 'danger' | 'accent' | 'utility';
}

export function DwellButton({ dwellSeconds, onActivate, className = '', tone = 'standard', children, ...props }: Props) {
  const [progress, setProgress] = useState(0);
  const timer = useRef<number | null>(null);
  const frame = useRef<number | null>(null);
  const startTime = useRef(0);
  const latched = useRef(false);

  const cancel = (resetLatch = true) => {
    if (timer.current) window.clearTimeout(timer.current);
    if (frame.current) cancelAnimationFrame(frame.current);
    timer.current = frame.current = null;
    setProgress(0);
    if (resetLatch) latched.current = false;
  };

  const begin = () => {
    if (props.disabled || latched.current || timer.current) return;
    startTime.current = performance.now();
    const animate = () => {
      setProgress(Math.min(1, (performance.now() - startTime.current) / (dwellSeconds * 1000)));
      frame.current = requestAnimationFrame(animate);
    };
    frame.current = requestAnimationFrame(animate);
    timer.current = window.setTimeout(() => {
      if (frame.current) cancelAnimationFrame(frame.current);
      timer.current = frame.current = null;
      setProgress(1);
      latched.current = true;
      onActivate();
    }, dwellSeconds * 1000);
  };

  const leave = () => cancel(true);
  const click = (event: MouseEvent<HTMLButtonElement>) => {
    if (latched.current) { event.preventDefault(); return; }
    cancel(false);
    latched.current = true;
    onActivate();
  };

  return <button {...props} type={props.type ?? 'button'} className={`dwell-button tone-${tone} ${className}`} style={{ '--dwell-progress': `${progress * 100}%`, ...props.style } as React.CSSProperties} onPointerMove={begin} onPointerLeave={leave} onFocus={begin} onBlur={leave} onPointerDown={() => cancel(false)} onClick={click}>{children}</button>;
}

